// Import Swiper core module.
import { act } from 'react';
import Swiper from 'swiper';

// Import Swiper modules.
// eslint-disable-next-line import/no-unresolved
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';

// Assign Swiper to window so Alpine can call on Swiper.
window.Swiper = Swiper;

/**
 * Expertises slider.
 */
export function initExpertisesSlider() {
	const sectionElement = document.querySelector('#section-expertises');
	if (!sectionElement) return;

	const sliderElement = sectionElement.querySelector('.swiper');
	if (!sliderElement) return;

	const swiper = new Swiper(sliderElement, {
		modules: [EffectFade, Navigation],
		loop: true,
		effect: 'fade',
		speed: 600,
		navigation: {
			prevEl: sliderElement.querySelector('.swiper-button--prev'),
			nextEl: sliderElement.querySelector('.swiper-button--next'),
		},
	});

	const listItems = sectionElement.querySelectorAll('ul li');
	const progressBarsDesktop = sectionElement.querySelectorAll('ul .progress span');
	const progressBarsMobile = sectionElement.querySelectorAll('.swiper-slide .progress span');

	const AUTOPLAY_DELAY = 5000;
	const TRANSITION_SPEED = parseInt(swiper.params.speed) || 600;
	const fullSlideDuration = AUTOPLAY_DELAY + TRANSITION_SPEED;

	let autoTimerId = null;
	let startTimestamp = 0;
	let isPaused = false;
	let remainingTime = fullSlideDuration;

	let rafId = null;

	function getBarsForIndex(index) {
		return [
			progressBarsDesktop[index],
			progressBarsMobile[index],
		].filter(Boolean);
	}

	function resetProgressBarsInstant() {
		cancelAnimationFrame(rafId);
		[...progressBarsDesktop, ...progressBarsMobile].forEach((pb) => {
			pb.style.transition = 'none';
			pb.style.width = '0%';
		});
	}

	function startProgressFromZero(index, duration) {
		cancelAnimationFrame(rafId);
		const bars = getBarsForIndex(index);
		let start = performance.now();

		function frame(now) {
			const t = Math.min((now - start) / duration, 1);
			const pct = t * 100;

			bars.forEach((pb) => {
				pb.style.transition = 'none';
				pb.style.width = `${pct}%`;
			});

			if (t < 1) {
				rafId = requestAnimationFrame(frame);
			}
		}
		rafId = requestAnimationFrame(frame);
	}

	function resumeProgressFromCurrent(index, duration) {
		cancelAnimationFrame(rafId);

		const bars = getBarsForIndex(index);

		if (duration <= 0) {
			bars.forEach(pb => pb.style.width = '100%');
			return;
		}

		const getCurrentPct = (pb) => {
			const parentWidth = pb.parentElement.offsetWidth || 1;
			const px = parseFloat(getComputedStyle(pb).width) || 0;
			return px / parentWidth;
		};

		let startPct = getCurrentPct(bars[0]);
		let start = performance.now();

		function frame(now) {
			const t = Math.min((now - start) / duration, 1);

			const pct = startPct + ((1 - startPct) * t);

			bars.forEach((pb) => {
				pb.style.transition = 'none';
				pb.style.width = `${pct * 100}%`;
			});

			if (t < 1) {
				rafId = requestAnimationFrame(frame);
			}
		}

		rafId = requestAnimationFrame(frame);
	}

	function clearAutoTimer() {
		if (autoTimerId) {
			clearTimeout(autoTimerId);
			autoTimerId = null;
		}
	}

	function startAutoForSlide(index, duration = fullSlideDuration) {
		clearAutoTimer();
		cancelAnimationFrame(rafId);

		startTimestamp = performance.now();
		remainingTime = duration;
		isPaused = false;

		startProgressFromZero(index, duration);

		autoTimerId = setTimeout(() => {
			autoTimerId = null;
			swiper.slideNext();
		}, duration);
	}

	function pauseAutoAndFreeze() {
		if (isPaused) return;
		isPaused = true;

		cancelAnimationFrame(rafId);

		const now = performance.now();
		const elapsed = now - startTimestamp;
		remainingTime = Math.max(remainingTime - elapsed, 0);

		clearAutoTimer();

		const index = swiper.realIndex;
		const bars = getBarsForIndex(index);

		bars.forEach((pb) => {
			const parentWidth = pb.parentElement.offsetWidth || 1;
			const widthPx = parseFloat(getComputedStyle(pb).width) || 0;
			const pct = widthPx / parentWidth;

			pb.style.transition = 'none';
			pb.style.width = `${pct * 100}%`;
		});
	}

	function resumeAutoFromRemaining() {
		if (!isPaused) return;

		const index = swiper.realIndex;
		isPaused = false;

		if (remainingTime <= 50) {
			swiper.slideNext();
			return;
		}

		resumeProgressFromCurrent(index, remainingTime);

		startTimestamp = performance.now();
		clearAutoTimer();

		autoTimerId = setTimeout(() => {
			autoTimerId = null;
			swiper.slideNext();
		}, remainingTime);
	}

	function updateActiveItem(index) {
		listItems.forEach((el, i) => {
			el.classList.toggle('active', i === index);
		});
	}

	listItems.forEach((item) => {
		const index = parseInt(item.getAttribute('data-slide'), 10);

		item.addEventListener('click', () => {
			swiper.slideToLoop(index);
			updateActiveItem(index);
			resetProgressBarsInstant();
			startAutoForSlide(index, fullSlideDuration);
		});

		item.addEventListener('mouseenter', () => {
			pauseAutoAndFreeze();
		});

		item.addEventListener('mouseleave', () => {
			resumeAutoFromRemaining();
		});
	});

	swiper.on('slideChange', () => {
		const idx = swiper.realIndex;
		updateActiveItem(idx);
		resetProgressBarsInstant();
		startAutoForSlide(idx, fullSlideDuration);
	});

	// Init
	updateActiveItem(swiper.realIndex);
	resetProgressBarsInstant();
	startAutoForSlide(swiper.realIndex, fullSlideDuration);
}

/**
 * Projects slider
 */
export function initProjectsSlider() {
	const sectionElement = document.querySelector('#section-projects');

	if (!sectionElement) {
		return;
	}

	const visualSliderElement = sectionElement.querySelector('.swiper.swiper-visual');

	if (!visualSliderElement) {
		return;
	}

	const textSliderElement = sectionElement.querySelector('.swiper.swiper-text');

	if (!textSliderElement) {
		return;
	}

	const linkElement = sectionElement.querySelector('.button--blue');

	const visualSlider = new Swiper(visualSliderElement, {
		modules: [Navigation],
		slidesPerView: 1,
		loop: true,
		speed: 600,
		navigation: {
			prevEl: visualSliderElement.querySelector('.swiper-button--prev'),
			nextEl: visualSliderElement.querySelector('.swiper-button--next'),
		},
	});

	const textSlider = new Swiper(textSliderElement, {
		modules: [EffectFade],
		slidesPerView: 1,
		effect: 'fade',
		fadeEffect: {
			crossFade: true
		},
		virtualTranslate: true,
		speed: 600,
		allowTouchMove: false,
		loop: true,
	});

	const updateProjectLink = () => {
        const realIndex = visualSlider.realIndex;

        textSlider.slideTo(realIndex);

        const activeElement = visualSliderElement.querySelector('.swiper-slide[data-swiper-slide-index="' +  realIndex + '"]');
		const indexElement = sectionElement.querySelector('.swiper-button--index .current');

        if (linkElement && activeElement) {
            const newPermalink = activeElement.getAttribute('data-permalink');

            if (newPermalink) {
                linkElement.href = newPermalink;
            }
        }

		if (indexElement) {
			indexElement.innerHTML = visualSlider.realIndex + 1;
		}
    };

	visualSlider.on('slideChange', updateProjectLink);
}

/**
 * Logo slider
 */
export function initLogosSlider() {
	const sliderElements = document.querySelectorAll('#section-header .swiper, #section-organisations .swiper');

	if (!sliderElements || sliderElements.length === 0) {
		return;
	}

	sliderElements.forEach(sliderElement => {
		const logos = sliderElement.querySelectorAll('.swiper-slide');

		if ( 2 >= logos.length ) {
			return;
		}

		const swiper = new Swiper(sliderElement, {
			modules: [Autoplay, Navigation],
			slidesPerView: 'auto',
			loop: true,
			spaceBetween: 40,
			speed: 7000,
			allowTouchMove: false,
			autoplay: {
				delay: 0,
				disableOnInteraction: false,
				pauseOnMouseEnter: false
			},
			longSwipesRatio: 0.1,
			breakpoints: {
				1280: {
					spaceBetween: 60,
				},
				1400: {
					spaceBetween: parseInt( sliderElement.getAttribute('data-gap') ),
				}
			},
		});
	});
}

/**
 * Project gallery
 */
export function initGallerySlider() {
	const sectionElement = document.querySelector('#section-gallery');

	if (!sectionElement) {
		return;
	}

	const sliderElement = sectionElement.querySelector('.swiper');

	if (!sliderElement) {
		return;
	}

	const swiper = new Swiper(sliderElement, {
		modules: [Navigation],
		slidesPerView: 'auto',
		loop: false,
		speed: 1000,
		spaceBetween: 20,
		centeredSlides: false,
		watchSlidesProgress: true,
		navigation: {
			prevEl: sliderElement.querySelector('.swiper-button--prev'),
			nextEl: sliderElement.querySelector('.swiper-button--next'),
		},
	});

	const updateIndex = () => {
		const indexElement = sectionElement.querySelector('.swiper-button--index .current');

		if (indexElement) {
			indexElement.innerHTML = swiper.realIndex + 1;
		}
    };

	swiper.on('slideChange', updateIndex);
}

/**
 * Team slider
 */
export function initTeamSlider() {
	const sectionElement = document.querySelector('#section-team');

	if (!sectionElement) {
		return;
	}

	const sliderElement = sectionElement.querySelector('.swiper');

	if (!sliderElement) {
		return;
	}

	const swiper = new Swiper(sliderElement, {
		modules: [Navigation],
		slidesPerView: 'auto',
		loop: false,
		speed: 600,
		spaceBetween: 20,
		centeredSlides: false,
		watchSlidesProgress: true,
		loopAdditionalSlides: 1,
		navigation: {
			prevEl: sliderElement.querySelector('.swiper-button--prev'),
			nextEl: sliderElement.querySelector('.swiper-button--next'),
		},
	});
}
